import { safeStorage } from '@patchbay/ui'
import { queryOptions } from '@tanstack/react-query'
import { newClientMessageId, toCitation } from '../components/palette-ask'
import { ApiError, describeError, unwrap } from '../lib/api/client'
import type { Audience, CardOut, ChatMessageOut, ChatThreadOut } from '../lib/api/generated'
import { chatGetApiV1ChatThreadIdGet, chatPostApiV1ChatThreadIdMessagesPost, chatStartApiV1ChatStartPost } from '../lib/api/generated'
import { formatWhen } from '../lib/display-time'
import type { ChatItem } from '../lib/types'
import { fallbackSearchItem } from './home-search'
import type { RileyCard } from './riley/contracts'
import { announceWalkthrough } from './riley/walkthrough-event'

/**
 * Home's cross-case Riley chat (round 2: agent-first home, mockup 07). One queue-scope thread per person
 * (`POST /chat/start {scope: "queue", resume: true}`); the thread id is remembered in this browser only
 * as a convenience. The command bar and the chat input post to the same thread: Riley answers with
 * text, or with confirm cards, and nothing runs until a person confirms the card (§6.2).
 */

const THREAD_KEY = 'patchbay-home-thread'

const at = (iso: string | undefined) => {
  if (!iso) return 'Just now'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'Just now' : formatWhen(d.toISOString(), { style: 'time' })
}

/** Unclassified is treated as customer-visible: an impact line must never understate who sees it. */
const IMPACT_AUDIENCE: Record<Audience, 'customer' | 'contractor' | 'internal'> = {
  customer: 'customer',
  contractor: 'contractor',
  vendor: 'contractor',
  internal: 'internal',
  unclassified: 'customer',
}

export function cardToItem(c: CardOut): ChatItem {
  const nobody = !c.impact_text?.trim()
  return {
    id: `card-${c.id}`,
    role: 'riley',
    at: 'Just now',
    // The real card: confirm/dismiss go to POST /cards/{id}/confirm|dismiss with this id.
    rileyCard: {
      id: c.id,
      kind: c.kind,
      title: c.title,
      audience: c.audience,
      executor: c.executor,
      impact_text: c.impact_text,
      preview: (c.preview ?? {}) as RileyCard['preview'],
      payload: (c.payload ?? {}) as RileyCard['payload'],
      scrub_report: (c.scrub_report ?? null) as RileyCard['scrub_report'],
      state: c.state as RileyCard['state'],
      can_confirm: c.can_confirm,
      editable: c.editable,
      result: (c.result ?? null) as RileyCard['result'],
      decided_at: c.decided_at ?? null,
      case_id: c.case_id ?? null,
      riley_card_id: c.riley_card_id ?? null,
      expires_at: c.expires_at ?? null,
      can_undismiss: c.can_undismiss,
    },
    card: {
      kind: c.kind,
      title: c.title || c.kind,
      impact: nobody ? { audience: 'nobody', text: '' } : { audience: IMPACT_AUDIENCE[c.audience], text: c.impact_text },
      executor: c.executor,
      scrubReport: c.scrub_report
        ? `Scrubbed: ${Object.entries(c.scrub_report)
            .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
            .join(', ')}`
        : undefined,
    },
  }
}

export function messageToItem(m: ChatMessageOut): ChatItem {
  return {
    id: `msg-${m.id}`,
    role: m.role === 'user' ? 'user' : 'riley',
    author: m.author ?? undefined,
    at: at(m.created_at),
    text: m.content_md,
    citations: (m.citations ?? []).map(toCitation),
    resultTables: m.result_tables,
    reportCards: m.report_cards,
  }
}

/** Riley's new turns since the person's message: every message after it, then the cards those messages raised. */
export function repliesAfter(thread: ChatThreadOut, userText: string): ChatItem[] {
  const msgs = thread.messages ?? []
  // The API echoes our message; find it by content from the end (client ids are not echoed in ChatMessageOut).
  let start = -1
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i]?.role === 'user' && msgs[i]?.content_md === userText) {
      start = i
      break
    }
  }
  const newer = msgs.slice(start + 1).filter((m) => m.role === 'riley' || m.role === 'system')
  const cardIds = new Set(newer.flatMap((m) => m.card_ids ?? []))
  const cards = (thread.cards ?? []).filter((c) => cardIds.has(c.id) && c.state === 'proposed')
  return [...newer.map(messageToItem), ...cards.map(cardToItem)]
}

/** Every message of a thread, oldest first, each followed by the cards it raised; unreferenced cards go last. */
export function threadToItems(thread: ChatThreadOut): ChatItem[] {
  const cards = new Map((thread.cards ?? []).map((c) => [c.id, c]))
  const placed = new Set<string>()
  const out: ChatItem[] = []
  for (const m of thread.messages ?? []) {
    out.push(messageToItem(m))
    for (const id of m.card_ids ?? []) {
      const c = cards.get(id)
      if (c && !placed.has(id)) {
        placed.add(id)
        out.push(cardToItem(c))
      }
    }
  }
  for (const c of thread.cards ?? []) if (!placed.has(c.id)) out.push(cardToItem(c))
  return out
}

/** The thread id this browser remembers, or null. Reading it never creates a thread. */
export function rememberedHomeThread(): string | null {
  return safeStorage.getItem(THREAD_KEY) || null
}

/**
 * The remembered thread's history (`GET /chat/{id}`, side-effect free). A thread the API no longer knows (404) is
 * forgotten and reads as "no conversation yet"; any other failure is thrown so the screen says it could not load.
 */
export const homeThreadQuery = (id: string | null) =>
  queryOptions({
    queryKey: ['chat', 'home', id],
    queryFn: async ({ signal }): Promise<ChatItem[] | null> => {
      if (!id) return null
      try {
        const thread = await unwrap(chatGetApiV1ChatThreadIdGet({ path: { thread_id: id }, signal }))
        return threadToItems(thread)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          safeStorage.setItem(THREAD_KEY, '')
          return null
        }
        throw err
      }
    },
    // Once per visit (see home.tsx): the screen appends what is sent, so a background refetch would duplicate lines.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: 1,
  })

/** The case's own Riley thread (scope case, resumed), so Riley reads that case rather than answering blind (#782). */
async function caseThreadId(caseId: string): Promise<string> {
  const t = await unwrap(chatStartApiV1ChatStartPost({ body: { scope: 'case', case_id: caseId, resume: true } }))
  return t.id
}

async function threadId(): Promise<string> {
  const remembered = safeStorage.getItem(THREAD_KEY)
  if (remembered) return remembered
  const t = await unwrap(chatStartApiV1ChatStartPost({ body: { scope: 'queue', resume: true } }))
  safeStorage.setItem(THREAD_KEY, t.id)
  return t.id
}

/** Posts one line to Riley. Always resolves: a failure becomes a Riley line saying what failed, followed by the
 * search fallback (the matching cases) so a question like "where is the yyzmt room phone case" still gets an answer. */
export async function sendHomeMessage(text: string, caseId?: string): Promise<ChatItem[]> {
  const clientMessageId = newClientMessageId()
  const post = (id: string) =>
    unwrap(
      chatPostApiV1ChatThreadIdMessagesPost({
        path: { thread_id: id },
        body: { message: text, client_message_id: clientMessageId },
        headers: { Accept: 'application/json' },
      }),
    )
  let failure: string
  try {
    let id = caseId ? await caseThreadId(caseId) : await threadId()
    let thread: ChatThreadOut
    try {
      thread = await post(id)
    } catch (err) {
      if (caseId) throw err
      // A remembered thread that the API no longer knows: start a fresh one once.
      if (!(err instanceof ApiError && err.status === 404)) throw err
      safeStorage.setItem(THREAD_KEY, '')
      id = await threadId()
      thread = await post(id)
    }
    if (thread.walkthrough !== undefined) announceWalkthrough(thread.walkthrough)
    const replies = repliesAfter(thread, text)
    if (replies.length) return replies
    if (!thread.riley_state || thread.riley_state === 'ok') {
      return [{ id: `r-${clientMessageId}`, role: 'riley', at: 'Just now', text: 'Riley is working on it.' }]
    }
    // Patchbay answered but Riley did not (degraded rail): the message was not delivered.
    failure = thread.riley_detail || `Riley is ${thread.riley_state.replace('_', ' ')}`
  } catch (err) {
    failure = describeError(err)
  }
  const said: ChatItem = {
    id: `err-${clientMessageId}`,
    role: 'riley',
    at: 'Just now',
    text: `Riley did not get this: ${failure}. Nothing was run or changed.`,
  }
  const found = await fallbackSearchItem(text, clientMessageId)
  return found ? [said, found] : [said]
}
